import { useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown.js';
import { getSegmentAggregatedData } from '../api/client';

/** Human-readable labels for each aggregated data column. */
const ATTRIBUTE_CARDS: { key: string; label: string }[] = [
  { key: 'coreIdentityPracticeContext', label: 'Core Identity & Practice Context' },
  { key: 'treatmentPhilosophyArchetype', label: 'Treatment Philosophy Archetype' },
  { key: 'regimenChoicePatterns1l', label: 'Regimen Choice Patterns (1L)' },
  { key: 'durationDeescalationSequencing', label: 'Duration, De-escalation & Sequencing' },
  { key: 'sharedDecisionMakingStyle', label: 'Shared Decision-Making Style' },
  { key: 'evidenceInformationPreferences', label: 'Evidence & Information Preferences' },
  { key: 'emotionalDriversFriction', label: 'Emotional Drivers & Friction' },
];

interface SegmentQualInsightsProps {
  segmentData: Record<string, unknown>;
  segmentName: string;
  editMode?: boolean;
}

/**
 * SegmentQualInsights — fetches segment aggregated data from the API
 * and displays a card for each HCP attribute column.
 */
export default function SegmentQualInsights({
  segmentData,
  segmentName,
}: SegmentQualInsightsProps): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(true);

  const segmentId = segmentData.id as number | undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['segment-aggregated-data', segmentId],
    queryFn: () => getSegmentAggregatedData(segmentId as number),
    enabled: segmentId != null,
  });

  const item = data?.item;

  // Build the list of cards that have a non-null value
  const cards = item
    ? ATTRIBUTE_CARDS.filter((c) => {
        const val = item[c.key];
        return typeof val === 'string' && val.length > 0;
      })
    : [];

  // Nothing to show (no data or all values null)
  if (!isLoading && cards.length === 0) {
    return null;
  }

  return (
    <Root>
      <Header onClick={() => setExpanded(!expanded)}>
        <span>HCP Attributes and Traits</span>
        <ExpandIcon $expanded={expanded}>
          <ExpandMoreIcon />
        </ExpandIcon>
      </Header>
      {expanded && (
        <Content>
          {isLoading ? (
            <LoadingContainer>
              <CircularProgress size={28} />
            </LoadingContainer>
          ) : (
            <CardGrid>
              {cards.map((c) => (
                <Card key={c.key}>
                  <CardLabel>{c.label}</CardLabel>
                  <CardValue>
                    <ChatMarkdown value={item?.[c.key] as string} />
                  </CardValue>
                </Card>
              ))}
            </CardGrid>
          )}
        </Content>
      )}
    </Root>
  );
}

const Root = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
`;

const Header = styled.div`
  padding: 8px 16px;
  font-size: 20px;
  font-weight: 700;
  font-family: var(--font-bold, inherit);
  color: var(--black-l, #323232);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  min-height: 64px;
  box-sizing: border-box;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  display: flex;
`;

const Content = styled.div`
  padding: 0 16px 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const CardLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardValue = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--black-l, #323232);
  white-space: pre-wrap;
`;
